import { updateWidgetData, updateDashboardKey, requestPlaylistPosition, routeSelected, widgetUserConfig, wsStatusPoll } from "./actions/index";
import moment from "moment";
import { nanoid } from "nanoid";
import _ from "lodash";
import { getCurrentVideoKey, videoKeyToTimestamp, absoluteTimeLookup, binarySearch, videoTimestampExtractor } from "../components/util/PlaylistUtils";
import { addWidget, changeWidgetStatus, removeWidget } from "./actions/widgetActions";
import { calculateBearingText, convertSpeed, parseRoutePositionData } from "../components/util/Geometry";
import { calculateFrame, findMostRecentMetadata, getAbsoluteTimestamp } from "../components/util/PlaylistUtils";

export const SOURCE_BLACKLIST = ["@devtools-page", "react-devtools-bridge", "react-devtools-content-script"];

export const MESSAGE_TYPES = {
    PARENT_CONNECTION: "PARENT_CONNECTION", //Called by child to connect to parent window
    CHILD_CONNECTION: "CHILD_CONNECTION", //Called by parent to connect to child window
    PARENT_REFRESHED: "PARENT_REFRESHED", //Sent by parent to trigger reconnection retry loop
    CHILD_CLOSE: "CLOSE", //Sent by parent to close a child
    CHILD_UNMOUNTED: "UNMOUNTED", //sent by child to parent to inform it that it is refreshing or closing
};

const speedAndBearingSelector = (state) => _.get(state.routeMetadata, ["SPEED_AND_BEARING", state.playlist.data.routeID], []);
const tachoDataSelector = (state) => _.get(state.routeMetadata, ["TACHO_DATA", state.playlist.data.routeID], []);

const isInIframe = () => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
};

const windowManager = () => {
    let current_token = null;
    let local_id = nanoid(6);
    let ws_state = {};
    let dashboardKeyData = {};
    let current_status = {
        type: "DASHBOARD",
        src: local_id,
        data: {
            routeID: null,
            position: null,
        },
    };
    let childWindows = [];
    let parentWindow = null;

    window.addEventListener("beforeunload", () => {
        sendMessage({
            type: MESSAGE_TYPES.PARENT_REFRESHED,
        });
    });

    const removeChildWindow = (window) => {
        const index = childWindows.indexOf(window);
        if (index > -1) {
            childWindows.splice(index, 1);
        }
    };

    const handleMessage = (store, message) => {
        if (SOURCE_BLACKLIST.includes(message.data?.source)) {
            return;
        }
        const messageType = message.data.type;
        switch (messageType) {
            case MESSAGE_TYPES.CHILD_CONNECTION:
                childWindows.push(message.source);
                message.source.postMessage({ type: MESSAGE_TYPES.PARENT_CONNECTION }, "*");

                if (!store.getState().widgets.hasOwnProperty(message.source.name)) {
                    store.dispatch(addWidget(message.source.widget));
                }
                store.dispatch(changeWidgetStatus(message.source.widget, true));
                store.dispatch(wsStatusPoll());
                break;
            case MESSAGE_TYPES.PARENT_CONNECTION:
                parentWindow = message.source;
                break;
            case MESSAGE_TYPES.PARENT_REFRESHED:
                if (!window.opener) {
                    removeChildWindow(message.source);
                } else {
                    setTimeout(() => {
                        connectToParent(true);
                    }, 1000);
                }
                break;
            case MESSAGE_TYPES.CHILD_CLOSE:
                window.close();
                break;
            case MESSAGE_TYPES.CHILD_UNMOUNTED:
                store.dispatch(removeWidget(message.data.widget.type, message.data.widget.id));
                break;
            default:
                onMessage(store, message);
        }
    };

    const sendMessage = (message, target) => {
        if (target) {
            const targetWindows = childWindows.filter((window) => window.widget.type === target.type && window.widget.id === target.id);
            let targetWindow;

            if (targetWindows.length > 0) {
                targetWindow = targetWindows[0];
                targetWindow.postMessage({ type: MESSAGE_TYPES.CHILD_CLOSE }, "*");
            }

            return;
        }

        if (parentWindow) {
            parentWindow.postMessage(message, "*");
        } else {
            childWindows.forEach((wind) => {
                wind.postMessage(message, "*");
            });
        }
    };

    const connectToParent = (forceNew = false) => {
        if (parentWindow && !forceNew) {
            return;
        }
        parentWindow = null;

        if (!window.opener) window.close();

        // I think there may be a race condition between the check above, and the postMessage below, where the window is closed before the message is sent,
        // so hopefully this prevents that from happening
        if (window.opener) {
            window.opener.postMessage({ type: MESSAGE_TYPES.CHILD_CONNECTION, widgetType: window.name }, "*");
        }

        setTimeout(() => {
            connectToParent();
        }, 2000);
    };

    const onMessage = (store, event) => {
        const payload = event.data;
        if (payload.action) {
            switch (payload.action) {
                case "request_position":
                    const state = store.getState();
                    const playlistPosition = state.playlist.position;
                    const playlistData = state.playlist.data;

                    let playlistIndex = -1;
                    let newOffset = 0;
                    const sourceIndex = playlistPosition.sourceIndex;

                    let timestamp = payload.timestamp;
                    if (timestamp > 9999999999) {
                        timestamp = timestamp / 1000;
                    }

                    const video = _.get(playlistData, ["video", sourceIndex], []);
                    if (video.length) {
                        playlistIndex = absoluteTimeLookup(timestamp, video);

                        if (playlistIndex !== -1) {
                            const newAbsoluteTimestamp = videoTimestampExtractor(video[playlistIndex]);
                            newOffset = timestamp - newAbsoluteTimestamp;
                        }
                        if (newOffset < 0) {
                            newOffset = 0;
                        }
                    } else {
                        const railImages = _.get(state, ["railInspection", "railInspectionImages", "data"], []);
                        playlistIndex = binarySearch(payload.timestamp, railImages, (image) => image.timestamp);
                    }
                    store.dispatch(
                        requestPlaylistPosition(
                            playlistPosition.isVideo,
                            playlistPosition.isEnhanced,
                            playlistPosition.isStills,
                            sourceIndex,
                            playlistIndex,
                            newOffset,
                        ),
                    );
                    break;
                case "update_widget_user_config":
                    store.dispatch(widgetUserConfig(payload.data));
                    break;
                case "request_map_position":
                    store.dispatch(routeSelected(payload.sessionID, payload.coords));
                    break;
                default:
                    break;
            }
        } else if (payload.src && payload.type && payload.data) {
            if (!window.location.href.includes("widget")) {
                return;
            }
            if (!ws_state[payload.type]) {
                ws_state[payload.type] = {};
            }
            ws_state[payload.type][payload.src] = {
                last_updated: moment.now(),
                state: payload.data,
            };
            store.dispatch(updateWidgetData(_.cloneDeep(ws_state)));
            store.dispatch(updateDashboardKey(payload.src));
            if (_.isEmpty(dashboardKeyData) || dashboardKeyData.last_message < Date.now() / 1000 - 60) {
                dashboardKeyData = {
                    key: payload.src,
                    last_message: Date.now() / 1000,
                };
            } else if (dashboardKeyData.key === payload.src) {
                dashboardKeyData.last_message = Date.now() / 1000;
            }
        }
    };

    // Processing function for all dispatch events
    return (store) => (next) => (action) => {
        const result = next(action);

        switch (action.type) {
            case "__INIT__":
                window.addEventListener("message", (e) => handleMessage(store, e));
                break;
            case "WS_SEND":
                sendMessage(action.msg, action.target);
                break;
            case "WS_STATUS_POLL":
                if (childWindows.length) {
                    const state = store.getState();
                    current_status.data.position = state.playlist.position;
                    current_status.data.routeID = _.get(state, ["playlist", "data", "routeID"], null);

                    let currentTimestamp = 0;

                    const selectedRailImage = _.get(state, ["railInspection", "selectedRailInspectionImage"]);
                    if (!_.isEmpty(selectedRailImage) && !_.isNil(selectedRailImage.timestamp)) {
                        currentTimestamp = selectedRailImage.timestamp;
                    } else {
                        const currentVideoKey = getCurrentVideoKey(state.playlist);

                        if (currentVideoKey) {
                            currentTimestamp = videoKeyToTimestamp(currentVideoKey) + state.playlist.position.currentTimeOffset * 1000;

                            if (!state.playlist.position.isStills && state.playlist.position.playerState === "playing") {
                                currentTimestamp += (Date.now() - state.playlist.position.currentTimeUpdateTime) * state.playlist.playbackSpeed; // "* speed of the player"
                            }
                        }
                    }

                    current_status.data.position.timestamp = currentTimestamp;
                    current_status.data.workspace_id = _.get(state, ["userDetails", "dashboardAccessID"], null);
                    current_status.data.position.railInspectionIndex = _.get(state, ["railInspection", "selectedRailInspectionImage", "index"], 0);
                    current_status.data.position.railInspectionLatPos = _.get(state, ["railInspection", "selectedRailInspectionImage", "latPos"], null);

                    sendMessage(current_status);
                } else if (current_token) {
                    console.log("[WS] Reconnecting");
                    // connectSocket(store);
                } else if (isInIframe()) {
                    const state = store.getState();
                    sendParentLocationData(state);
                }
                break;
            case "WS_WIDGET_PING":
                sendMessage({
                    type: "WIDGET",
                    src: local_id,
                    data: {
                        widgetType: action.widgetType,
                    },
                });
                break;
            case "CONNECT_TO_PARENT":
                connectToParent();
                break;
            case "PARENT_REFRESHED":
                sendMessage({
                    type: MESSAGE_TYPES.PARENT_REFRESHED,
                });
                break;
            case "USER_CONFIG":
                sendMessage({
                    action: "update_widget_user_config",
                    data: action.userConfig,
                });
                break;
            default:
                break;
        }
        return result;
    };
};

const sendParentLocationData = _.throttle((state) => {
    const playlistState = state.playlist;

    const currentIndex = _.get(playlistState, ["position", "currentIndex"]);
    const latLonCoords = _.get(playlistState, ["position", "coords"]);
    const currentTimeOffset = _.get(playlistState, ["position", "currentTimeOffset"]);
    const routeID = _.get(playlistState, ["data", "routeID"]);
    const isVideo = _.get(playlistState, ["position", "isVideo"]);
    const sourceIndex = _.get(playlistState, ["position", "sourceIndex"]);
    const routeSystemID = _.get(playlistState, ["data", "system_id"]);

    const playingPlaylist = isVideo ? _.get(state.playlist.data, ["video", sourceIndex], []) : state.playlist.data.image;
    const absoluteTimestamp = getAbsoluteTimestamp(routeID, playingPlaylist, currentIndex, isVideo, currentTimeOffset);

    const speedMetadata = speedAndBearingSelector(state);
    const tachoMetadata = tachoDataSelector(state);

    const tachoInfo = findMostRecentMetadata(tachoMetadata, absoluteTimestamp);
    const speedInfo = findMostRecentMetadata(speedMetadata, absoluteTimestamp);

    let speed = "Unknown";
    let bearing = "Unknown";
    let baseSpeed = null;

    const tachoSpeed = _.get(tachoInfo, ["data", "speed"]);
    const metadataSpeed = _.get(speedInfo, ["data", "speed"]);

    if (!_.isNil(tachoSpeed) && tachoInfo.timestamp > absoluteTimestamp - 5) {
        baseSpeed = tachoSpeed;
    }

    if (baseSpeed === null && !_.isNil(metadataSpeed) && speedInfo.timestamp > absoluteTimestamp - 5) {
        baseSpeed = metadataSpeed;
    }

    if (baseSpeed !== null) {
        speed = Math.round(convertSpeed(baseSpeed, "miles"));
    }
    if (!_.isNil(speedInfo?.data.bearing)) {
        bearing = calculateBearingText(_.get(speedInfo, ["data", "bearing"]));
    }
    const frameNumber = calculateFrame(playingPlaylist, currentIndex, currentTimeOffset);
    const currentSession = _.get(state, ["sessions", routeID]);
    const snapshotKey = `${currentSession.device_uuid}/${currentSession.uuid}/${_.get(playingPlaylist, [currentIndex, 0])}:${frameNumber || 1}`;

    const currentRoutePosition = state.playlist.position.routePosition;
    const nearestLocation = parseRoutePositionData(currentRoutePosition, routeSystemID);

    let elr_data = null;

    if (nearestLocation) {
        elr_data = {
            ELR: nearestLocation.elr,
            MILE: nearestLocation.sign * nearestLocation.mile,
            YARD: nearestLocation.yard,
            TRACK: nearestLocation.track,
            SOURCES: nearestLocation.sources,
        };
    }

    const location_object = {
        TYPE: "LOCATION",
        DATA: {
            LAT: _.get(latLonCoords, [1]),
            LON: _.get(latLonCoords, [0]),
            TIME: absoluteTimestamp,
            ELR: elr_data,
            SPEED: speed,
            BEARING: bearing,
            SNAPSHOT_KEY: snapshotKey,
            SESSION_ID: routeID,
        },
    };

    window.parent.postMessage(location_object, "*");
}, 500);

export default windowManager();
